<script>
import * as m from '@app/paraglide/messages.js';
import {
    eventGroupState,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { commitHistory } from "@app/store/historyStore.svelte.js";
import { audioScrub } from "@app/store/audioScrubStore.svelte.js";
import { onMount } from 'svelte';
import tcLib from "@cclib/lib/timecode.js";

/**
 * @typedef {Object} Props
 * @property {any} event
 * @property {string} [tcType]
 * @property {boolean} [enabled]
 */

/** @type {Props} */
let { event, tcType = "start", enabled = true } = $props();
let tc = $state("--:--:--:--");
let canEdit = $derived(enabled && !isSelectedEventGroupLocked());

onMount(() =>{
    try {
        if (
            !event[tcType] &&
            event[tcType] !== 0
        ) {
            tc = "--:--:--:--";
        } else {
            tc = tcLib.secToTc(
                event[tcType] + projectState.incode,
                projectState.frameRate,
                projectState.dropFrame,
            );
        }
    } catch (err) {
        tc = "--:--:--:--";
    }
});


function setTc(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!canEdit) {
        return;
    }

    let originalDuration =
        event.end ===
            false ||
        event.start ===
            false
            ? 2
            : event.end -
              event.start;

    let originalId =
        event.id;

    const egIdx = projectState.selected;
    const eventIndex = eventGroupState[egIdx].events.findIndex((e) => e.id === originalId);
    const playerTime = player.currentTime;
    const minFrameGap = editorState.minFrameGap;

    tc = tcLib.secToTc(
        playerTime + projectState.incode,
        projectState.frameRate,
        projectState.dropFrame,
    );

    commitHistory({
        name: "update timecode",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                const ev = draft[egIdx].events[eventIndex];
                if (!ev) return;
                ev[tcType] = playerTime;

                if (tcType === "start") {
                    if (isNaN(ev.end)) {
                        ev.end = playerTime + 2;
                    } else if (ev.end < ev.start) {
                        ev.end = ev.start + originalDuration;
                    }

                    if (
                        minFrameGap > 0 &&
                        eventIndex > 0 &&
                        ev.start <= draft[egIdx].events[eventIndex - 1].end
                    ) {
                        draft[egIdx].events[eventIndex - 1].end =
                            ev.start - minFrameGap / projectState.frameRate;
                    }
                } else if (tcType === "end") {
                    if (isNaN(ev.start)) {
                        ev.start = playerTime - 2;
                    } else if (ev.start > ev.end) {
                        ev.start = ev.end - originalDuration;
                    }

                    if (
                        minFrameGap > 0 &&
                        eventIndex < draft[egIdx].events.length - 1 &&
                        ev.end >= draft[egIdx].events[eventIndex + 1].start
                    ) {
                        draft[egIdx].events[eventIndex + 1].start =
                            ev.end + minFrameGap / projectState.frameRate;
                    }
                }

                const newIndex = draft[egIdx].events.findIndex(
                    (item) => item.id === originalId,
                );
                draft[egIdx].selected = [newIndex];
            },
        },
    });

    e.target.blur();
}

function validateTc(e) {
    if (e.target.value === "--:--:--:--") {
        return;
    }

    if (!canEdit) {
        return;
    }

    tc = tcLib.tcValidate(
        e.target.value,
        projectState.frameRate,
        projectState.dropFrame,
    );

    const egIdx = projectState.selected;
    const eventId = event.id;
    const nextTc =
        tcLib.tcToSec(tc, projectState.frameRate, projectState.dropFrame) -
        projectState.incode;

    commitHistory({
        name: "update timecode",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                const idx = draft[egIdx].events.findIndex((item) => item.id === eventId);
                if (idx > -1) {
                    draft[egIdx].events[idx][tcType] = nextTc;
                }
            },
        },
    });

    e.target.blur();
}

function updateCurrentTime(e) {
    e.preventDefault();
    e.stopPropagation();
    if (lockState.video && player) {
        player.currentTime =
            event[tcType];
    }
}

function frameByFrame(e) {
    if (!canEdit) {
        return;
    }

    if (e.key === "ArrowUp") {
        event[tcType] +=
            tcLib.framesToSec(1, projectState.frameRate);
        tc = tcLib.secToTc(
            event[tcType] + projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        );

        if (lockState.video && player) {
            player.currentTime =
                event[
                    tcType
                ];
            audioScrub();
        }
    } else if (e.key === "ArrowDown") {
        event[tcType] -=
            tcLib.framesToSec(1, projectState.frameRate);
        tc = tcLib.secToTc(
            event[tcType] + projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        );

        if (lockState.video && player) {
            player.currentTime =
                event[
                    tcType
                ];
            audioScrub();
        }
    }
}

function updateTc(sec) {
    if (!sec && sec !== 0) {
        tc = "--:--:--:--";
        return;
    }

    tc = tcLib.secToTc(
        (parseFloat(sec) + parseFloat(projectState.incode)).toFixed(3),
        projectState.frameRate,
        projectState.dropFrame,
    );
}

function triggerTcUpdate(frameRate, incode) {
    if (event[tcType] === undefined || event[tcType] === null) {
        return;
    }

    updateTc(event[tcType]);
}

$effect(() => {
    // console.log("EFFECT Syncing TC Input with Event Group State...");
    triggerTcUpdate(projectState.frameRate, projectState.incode);
});
</script>

<button
    id="tc-set-button-{tcType}"
    aria-label={m.input_tc_set_button_aria_label({tcType})}
    disabled={!canEdit}
    class="btn btn-secondary"
    type="button"
    title={m.input_tc_set_button_title({tcType})}
    onclick={setTc}>
    <i class="bi bi-clock"></i></button>
<input
    id="tc-input-{tcType}"
    aria-label={m.input_tc_input_aria_label({tcType})}
    disabled={!canEdit}
    class="form-control border"
    type="text"
    onclick={(e) => updateCurrentTime(e)}
    onkeydown={frameByFrame}
    placeholder={tcType}
    bind:value={tc}
    onblur={validateTc}
    onkeyup={(e) => e.key === "Enter" && validateTc(e)}
    onfocus={(e) => {
        e.target.select();
    }}
    title={tc} />

<style>
button,
input {
    font-size: 0.9vw !important;
    padding: 3px 6px !important;
}
</style>
